<!-- eslint-disable vue/no-v-text-v-html-on-component -->
<!-- eslint-disable vue/no-v-html -->
<template>
  <layout disable-sticky-header hide-search-cta>
    <div class="Search">
      <div class="Layout Search-headerWrapper">
        <div class="Layout-section">
          <ai-separator :margin="2" class="Search-headerSeparator" />
        </div>

        <ai-breadcrumb :breadcrumb-items="breadcrumbItems" variant="positive" />
      </div>

      <ai-typo
        as="h1"
        class="Search-availabilityFilters-title"
        variant="heading-03">
        {{ t('search.title.findYourDreamDestination') }}
      </ai-typo>

      <search-availability-filters ref="searchFilters" />

      <section class="Search-result Layout">
        <div class="Layout-section">
          <ai-typo as="h2" class="sr-only">
            {{ t('search.title.resultsTitle') }}
          </ai-typo>

          <template v-if="searchStoreLoading">
            <div class="Search-resultTitle">
              <ai-skeleton
                desktop-height="24px"
                desktop-width="230px"
                mobile-height="24px"
                mobile-width="230px" />
            </div>
            <skeleton-search-result-card
              v-for="n of 3"
              :key="n"
              class="Search-hotelCard" />
          </template>
          <template v-else-if="resultCount">
            <div aria-live="assertive" class="Search-resultTitle">
              <ai-typo
                as="p"
                variant="paragraph-03-regular"
                v-html="
                  t('search.title.searchResults', { count: resultCount })
                " />
            </div>

            <div
              v-if="displayNoAvailabilityAlert"
              class="Search-availabilityWarning">
              <ai-icon
                class="Search-availabilityWarning--icon"
                name="warning" />
              <ai-typo as="p" variant="paragraph-04-bold">
                {{ t('search.alert.noAvailability.title') }}
              </ai-typo>
              <div />
              <ai-typo as="p" variant="paragraph-04-regular">
                {{ t('search.alert.noAvailability.description') }}
              </ai-typo>
            </div>

            <ul class="Search-result-list" data-testid="hotelSearchResultList">
              <search-hotel-card
                v-for="hotel in searchStore.eligibleHotels"
                :key="hotel.ridcode"
                :adults="
                  searchStore.currentSearchVariables.rooms?.[0]?.adults ?? 2
                "
                :brand="hotel.brand"
                :children-ages="
                  searchStore.currentSearchVariables.rooms?.[0]?.childrenAges ??
                  []
                "
                :city="hotel.city"
                :country="hotel.country"
                :country-code="hotel.countryCode as string"
                :currency="hotel.bestOffer?.currency"
                :date-in="searchStore.dateIn"
                :destination-id="hotel.expediaId"
                :hotel="hotel"
                :hotel-id="hotel.ridcode"
                :hotel-name="hotel.name"
                :image="hotel.galleryImages?.[0]"
                :images="
                  (hotel.galleryImages?.length || 0) > 1
                    ? hotel.galleryImages
                    : undefined
                "
                :include-flights="searchStore.includeFlights"
                :loading="loading && selectedHotel === hotel.ridcode"
                :member-meal-plan="
                  // eslint-disable-next-line vue/no-deprecated-filter
                  hotel.bestOffer?.memberMealPlan as string | undefined
                "
                :member-price="hotel.bestOffer?.memberPrice"
                :nights="searchStore.currentSearchVariables.dates?.nights"
                :pictograms="
                  hotel.experienceCategories?.map(category => ({
                    label: category.title ?? '',
                    url: category.logo?.src ?? '',
                  }))
                "
                :public-meal-plan="
                  // eslint-disable-next-line vue/no-deprecated-filter
                  hotel.bestOffer?.publicMealPlan as string | undefined
                "
                :public-price="
                  hotel.bestOffer?.publicPrice ||
                  hotel.bestOffer?.preferredOfferPrice
                "
                :rating="hotel.rating"
                :reviews="hotel.nbReviews"
                :slug="hotel.slug"
                :target-currency="bookingStore.currentCurrency"
                :zip-code="hotel.zipCode as string"
                class="Search-hotelCard"
                role="listitem"
                @booking-action="goToBooking"
                @flight-action="goToHotelWithFlight"
                @open-datepicker="openDatepicker"
                @search-flight-action="goToSearchFlightsHotel" />
            </ul>

            <template v-if="searchStore.eligibleAlternativeHotels.length">
              <ai-typo as="h2" class="Search-resultTitle" variant="heading-03">
                {{
                  t('search.title.alternativeHotels', {
                    experienceCategory:
                      searchStore.selectedExperienceCategoryTitle,
                  })
                }}
              </ai-typo>
              <search-hotel-card
                v-for="hotel in searchStore.eligibleAlternativeHotels"
                :key="hotel.ridcode"
                :adults="
                  searchStore.currentSearchVariables.rooms?.[0]?.adults ?? 2
                "
                :brand="hotel.brand"
                :children-ages="
                  searchStore.currentSearchVariables.rooms?.[0]?.childrenAges ??
                  []
                "
                :city="hotel.city"
                :country="hotel.country"
                :country-code="hotel.countryCode as string"
                :currency="hotel.bestOffer?.currency"
                :date-in="searchStore.dateIn"
                :destination-id="hotel.expediaId"
                :hotel="hotel"
                :hotel-id="hotel.ridcode"
                :hotel-name="hotel.name"
                :image="hotel.galleryImages?.[0]"
                :include-flights="searchStore.includeFlights"
                :loading="loading && selectedHotel === hotel.ridcode"
                :member-meal-plan="
                  // eslint-disable-next-line vue/no-deprecated-filter
                  hotel.bestOffer?.memberMealPlan as string | undefined
                "
                :member-price="hotel.bestOffer?.memberPrice"
                :nights="searchStore.currentSearchVariables.dates?.nights"
                :pictograms="
                  hotel.experienceCategories?.map(category => ({
                    label: category.title ?? '',
                    url: category.logo?.src ?? '',
                  }))
                "
                :public-meal-plan="
                  // eslint-disable-next-line vue/no-deprecated-filter
                  hotel.bestOffer?.publicMealPlan as string | undefined
                "
                :public-price="hotel.bestOffer?.publicPrice"
                :rating="hotel.rating"
                :reviews="hotel.nbReviews"
                :slug="hotel.slug"
                :target-currency="currentCurrency"
                :zip-code="hotel.zipCode as string"
                class="Search-hotelCard"
                role="listitem"
                @booking-action="goToBooking"
                @flight-action="goToHotelWithFlight"
                @open-datepicker="openDatepicker"
                @search-flight-action="goToSearchFlightsHotel" />
            </template>
          </template>

          <booking-help-center />

          <ai-legal-mentions-price-legal-mention
            :lang="locale"
            terms-type="priceLegalMention" />
        </div>
      </section>
    </div>
  </layout>
</template>

<script lang="ts" setup>
import { useApolloClient } from '@vue/apollo-composable';
import { differenceInDays } from 'date-fns';
import { storeToRefs } from 'pinia';

import { useBookingStore, useBookingUrlGenerator } from '~/domains/booking';
import {
  useFlightHotelAvailability,
  useFlightHotelUrlGenerator,
  useFlightHotelValidator,
} from '~/domains/feature';
import {
  SearchDestinationResultType,
  ValidateRoomsDocument,
} from '~/domains/graphql';
import { useSearchSeo, useSearchStore } from '~/domains/search';
import type {
  BookingActionParams,
  FlightActionParams,
  SearchFlightActionParams,
} from '~/domains/search/components/SearchHotelCard/SearchHotelCard.vue';
import { useLayoutStore } from '~/domains/ux';
import {
  ecommerce,
  extractDateFromApiString,
  formatDate,
  pageView,
} from '~/helpers';

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: '/search',
    fr: '/recherche',
    de: '/suche',
    ru: '/поиск',
    tr: '/arama',
  },
});
const { t, locale } = useI18n();

useHead({
  meta: [{ name: 'robots', content: 'noindex, follow' }],
});

await useSearchSeo(t);

const loading = ref(false);
const selectedHotel = ref<string | null>(null);

const bookingStore = useBookingStore();
const searchStore = useSearchStore();
const layoutStore = useLayoutStore();
const { client } = useApolloClient();
const { bookingURL } = useBookingUrlGenerator();
const { generateUrl: expediaUrl } = useFlightHotelUrlGenerator(
  locale,
  'searchCard',
);

const { eligibleHotels: eligibleHotelsRef } = storeToRefs(searchStore);

const route = useRoute();
const { scrollToTop } = useScrollToTop();
const localePath = useLocalePath();
const { originError, validateFlightHotelParams } = useFlightHotelValidator();
const { isFlightHotelAvailable } = useFlightHotelAvailability();

const searchStoreLoading = computed(() => searchStore.loading);
const searchStoreError = computed(() => searchStore.fetchError);

watch(searchStoreLoading, loading => {
  if (!loading) return;

  scrollToTop();
});

watch(searchStoreError, () => {
  layoutStore.pauseSkeletons = Boolean(searchStoreError.value);

  showError({
    fatal: true,
    message: searchStoreError.value?.message,
    data: { page: 'search', error: searchStoreError.value },
  });
});

const breadcrumbItems = [
  {
    label: t('navigation.page.home'),
    route: localePath({ name: 'index' }),
  },
  {
    label: t('navigation.page.search'),
    route: localePath({ name: 'search' }),
    currentPage: true,
  },
];

onMounted(() => {
  const { dates, destination, roomsAndGuests, includeFlights, origin } =
    route.query;

  if (dates) {
    const [rawFirstDate, rawLastDate] = JSON.parse(dates as string);

    if (rawFirstDate) {
      const firstDate = extractDateFromApiString(rawFirstDate);

      firstDate && (searchStore.dates = [firstDate]);
    }

    if (rawLastDate) {
      const lastDate = extractDateFromApiString(rawLastDate);

      lastDate && searchStore.dates.push(lastDate);
    }
  } else {
    searchStore.dates = [];
  }

  if (destination) {
    const { text, type, ridcode } = JSON.parse(destination as string);
    searchStore.destination = { text, type, ridcode };
  } else {
    searchStore.destination = {
      text: '',
      type: undefined,
    };
  }

  if (roomsAndGuests) {
    const rooms = JSON.parse(roomsAndGuests as string);
    searchStore.rooms = [];

    for (const room of rooms) {
      searchStore.rooms.push({
        adults: room.adults,
        childrenAges: room.childrenAges,
      });
    }
  } else {
    searchStore.rooms = [];
  }

  searchStore.includeFlights =
    isFlightHotelAvailable && includeFlights === 'true';

  setRouteOriginToStore(origin);

  searchStore.searchHotels(client);
});

const setRouteOriginToStore = (origin?: any) => {
  if (origin) {
    const { text, type, airportCode, regionId } = JSON.parse(origin);

    if (text || type || airportCode || regionId) {
      searchStore.origin = { text, type, airportCode, regionId };
    }
  } else {
    searchStore.origin = { text: '' };
  }
};

const resultCount = computed(() => searchStore.eligibleHotels.length);
const currentCurrency = computed(() => bookingStore.currentCurrency);

const displayNoAvailabilityAlert = computed(() => {
  const withOffers = Boolean(searchStore.currentSearchVariables?.dates?.dateIn);
  const eligibleHotelsHasOffer = searchStore.eligibleHotels.some(hotel =>
    Boolean(
      hotel.bestOffer?.memberPrice ||
        hotel.bestOffer?.publicPrice ||
        hotel.bestOffer?.preferredOfferPrice,
    ),
  );

  return withOffers && !eligibleHotelsHasOffer;
});

watch(eligibleHotelsRef, newHotels => {
  if (!newHotels || newHotels.length === 0) {
    return;
  }

  const items = newHotels.map(function (hotel, index) {
    const itemListId = `${hotel.countryCode} - ${hotel.zipCode}`;
    const itemListName = `${hotel.country} - ${hotel.city}`;
    return {
      index,
      item_brand: hotel.brand,
      item_category: 'room',
      item_category4: hotel.zipCode ?? '',
      item_id: hotel.ridcode,
      item_name: hotel.name,
      location_id: hotel.countryCode ?? '',
      item_variant: '',
      item_list_name: itemListName,
      item_list_id: itemListId,
      price:
        hotel.bestOffer?.memberPrice ??
        (hotel.bestOffer?.publicPrice as number | undefined),
      curreny: hotel.bestOffer?.currency as number | undefined,
    };
  });

  const dataEcommerceEvent = searchStore.ecommerceEvent;

  ecommerce(
    'view_item_list',
    {
      currency: bookingStore.currentCurrency,
      ...dataEcommerceEvent,
      items,
    },
    'Ecommerce - Item List Views',
  );
});

pageView({
  page_category: 'hotel listing (search & filter) - list view',
  page_name: 'results::standard::list',
  page_sub_category_level_1: 'results',
  page_sub_category_level_2: 'standard',
  page_sub_category_level_3: 'list',
});

const searchFilters = ref();

const focusOriginField = () => {
  if (!searchFilters.value) return;

  searchFilters.value.focusOriginField();
};

const openDatepicker = () => {
  if (!searchFilters.value) return;

  searchFilters.value.openDatepicker();
};

const goToBooking = async (params: BookingActionParams) => {
  const hotelIndex = searchStore.eligibleHotels.findIndex(
    eHotel => eHotel.ridcode === params.hotelId,
  );

  const hotel = hotelIndex >= 0 ? searchStore.eligibleHotels[hotelIndex] : null;

  const items = [
    {
      index: hotelIndex,
      item_brand: hotel?.brand,
      item_category: 'room',
      item_category4: hotel?.zipCode ?? '',
      item_id: hotel?.ridcode,
      item_name: hotel?.name,
      location_id: hotel?.countryCode ?? '',
      item_list_id: `${hotel?.countryCode} - ${hotel?.zipCode}`,
      item_list_name: `${hotel?.country} - ${hotel?.city}`,
      price:
        hotel?.bestOffer?.memberPrice ??
        (hotel?.bestOffer?.publicPrice as number | undefined),
      curreny: hotel?.bestOffer?.currency as number | undefined,
    },
  ];

  const dataEcommerceEvent = searchStore.ecommerceEvent;

  ecommerce(
    'select_item',
    {
      currency: bookingStore.currentCurrency,
      ...dataEcommerceEvent,
      location_id: hotel?.countryCode as string | undefined,
      items,
    },
    'Ecommerce - Select Item',
  );

  const dateIn =
    searchStore.currentSearchVariables.dates?.dateIn ?? searchStore.dateIn;
  const nights =
    searchStore.currentSearchVariables.dates?.nights ?? searchStore.nbNights;
  bookingStore.hotelDetails.city = hotel?.city;
  bookingStore.hotelDetails.cityCode = hotel?.zipCode ?? '';
  bookingStore.hotelDetails.country = hotel?.country ?? '';
  bookingStore.hotelDetails.countryCode = hotel?.countryCode ?? '';
  bookingStore.hotelDetails.hotelName = hotel?.name ?? '';
  bookingStore.hotelDetails.brandCode = hotel?.brand ?? '';

  selectedHotel.value = params.hotelId;
  loading.value = true;

  const result = await client.query({
    query: ValidateRoomsDocument,
    variables: {
      dateIn,
      nights,
      hotelCode: params.hotelId,
      rooms: searchStore.rooms,
    },
    fetchPolicy: 'no-cache',
  });

  let rooms = searchStore.rooms;

  if (rooms.length === 0) {
    rooms = [{ adults: 2, childrenAges: [] }];
  }

  let bookingUrl = bookingURL(rooms, params.hotelId, dateIn, nights);

  if (!result.data.validateRooms) {
    bookingUrl += '&error=invalid-rooms';
  }

  navigateTo(bookingUrl);
};

const goToHotelWithFlight = (params: FlightActionParams) => {
  const { dates, roomsAndGuests } = route.query;

  const queryParams = {
    dates,
    roomsAndGuests,
    includeFlights: isFlightHotelAvailable ? 'true' : undefined,
  };

  const path = localePath({
    name: 'hotel-hotelSlug',
    params: { hotelSlug: params.hotelSlug },
    query: queryParams,
  });

  if (path) {
    navigateTo(path);
  }
};

const goToSearchFlightsHotel = (params: SearchFlightActionParams) => {
  validateFlightHotelParams(undefined, undefined, searchStore.origin);

  if (originError.value) {
    focusOriginField();

    return;
  }

  const url = expediaUrl(
    searchStore.origin,
    {
      text: params.hotelName,
      type: SearchDestinationResultType.Hotel,
      ridcode: params.hotelId,
      destinationId: params.destinationId,
    },
    {
      dateIn: formatDate(searchStore.dates[0], 'yyyy-MM-dd'),
      nights: differenceInDays(searchStore.dates[1], searchStore.dates[0]),
      roomsAndGuests: searchStore.rooms,
    },
  );

  window.open(url, '_self');
};
</script>

<style lang="scss" scoped>
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/mq';

.Search {
  background-color: colors.$white;
}

.Search-headerWrapper {
  margin-top: constants.$header;
  background-color: colors.$white;
}

.Search-headerSeparator {
  width: 100%;
}

.Search-availabilityFilters-title {
  text-align: center;
  margin-bottom: constants.$margin-02;

  @media (mq.$from-medium) {
    margin-bottom: constants.$margin-01;
  }
}

.Search-result-list {
  width: 100%;
}

.Search-result {
  display: flex;
  flex-direction: column;
  gap: constants.$margin-02;
  padding: 0 constants.$padding-02;
  background-color: colors.$neutral-100;

  @media (mq.$from-medium) {
    padding: 0 constants.$padding-05;
  }
}

.Search-error {
  margin-top: constants.$margin-02;
}

.Search-hotelCard {
  width: 100%;
  margin-bottom: constants.$margin-01;
}

.Search-resultTitle {
  width: 100%;
  margin: constants.$margin-02 0;
}

.Search-availabilityWarning {
  width: 100%;
  margin-bottom: constants.$margin-02;
  display: grid;
  grid-template-columns: 3em 1fr;
  background: colors.$aqua-blue-100;
  padding: constants.$padding-01;
  gap: constants.$inner-01;
}

.Search-availabilityWarning--icon {
  justify-self: center;
  align-items: center;
  display: inline-grid;
}
</style>
